'use client';

import { c as _c } from "react/compiler-runtime";
import type { FactiiiRequirement } from '@shared/all/domains/factiiis';
import { addFactiiiReducer, collectRequirements, initialFormState, rootNeedsRule, toAddInput } from '@shared/utils/helpers/addFactiii/formState';
import { findSection, type SectionKey } from '@shared/utils/helpers/addFactiii/sections';
import { CUSTOM_TEMPLATE, FACTIII_TEMPLATES, type FactiiiTemplate } from '@shared/utils/helpers/addFactiii/templates';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, CornerDownRight, Plus } from 'lucide-react';
import { useReducer, useState } from 'react';
import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import FactiiiForm from './addFactiii/FactiiiForm';
import OptionsView from './addFactiii/OptionsView';
import { t } from './addFactiii/parts';
import { SECTION_BODIES } from './addFactiii/sections';
import TemplateGallery from './addFactiii/TemplateGallery';
type Props = {
  closeHandler: (factiii?: RouterOutputs['factiiis']['add']) => void;
  type: 'USER' | 'SPACE';
  slug: string;
  refetch: () => void;
  searchedName?: string;
  // Requirements always applied to the created factiii (e.g. LIST when added
  // from a board), on top of whatever the user selects.
  forcedRequirements?: FactiiiRequirement[];
  // Land on one template's form instead of the gallery. For a caller that
  // already knows the shape it wants. The vault's Factiiis tab asks for
  // 'root', so the form arrives with that template's labels and placeholders
  // rather than the generic ones `forcedRequirements` alone would give.
  templateKey?: string;
  // The factiii this one will hang under, when the caller attaches it after
  // creating (the vault's tree). Shown so the form reads as "a sub factiii of
  // X", not a fresh top-level factiii — the modal itself draws no edge.
  parent?: {
    id: number;
    name: string;
    avatarKey: string | null;
  };
};

// The modal is a small navigation stack, not one long form: gallery → form →
// options → one setting. Every screen holds what fits on it, and the header's
// back arrow is the only way back, so nothing ever expands in place.
type View = {
  name: 'gallery';
} | {
  name: 'form';
} | {
  name: 'options';
} | {
  name: 'section';
  key: SectionKey;
  from: 'form' | 'options';
};

/**
 * Creating a factiii: pick a template, name it, press Create.
 *
 * The template only seeds the form — every setting stays reachable and
 * optional whichever one you pick — so the gallery is a shortcut past the
 * vocabulary, not a branch in the flow. A caller that already fixed the shape
 * (a board list, via `forcedRequirements`) skips the gallery and lands on the
 * form.
 */
const AddFactiiiModal = props => {
  const $ = _c(85);
  const {
    addFactiii
  } = useFactiiis(client);
  const forcedRequirements = props.forcedRequirements ?? [];
  let t0;
  if ($[0] !== props.templateKey) {
    t0 = props.templateKey ? FACTIII_TEMPLATES.find(t$0 => t$0.key === props.templateKey) : undefined;
    $[0] = props.templateKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const named = t0;
  const preset = forcedRequirements.length > 0 || named !== undefined;
  const [template, setTemplate] = useState(named ?? CUSTOM_TEMPLATE);
  let t1;
  if ($[2] !== preset) {
    t1 = preset ? {
      name: "form"
    } : {
      name: "gallery"
    };
    $[2] = preset;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [view, setView] = useState(t1);
  const [submitting, setSubmitting] = useState(false);
  const t2 = props.searchedName ?? "";
  const t3 = props.type === "SPACE" ? props.slug : "";
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = initialFormState(t2, t3);
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== named) {
    t5 = init => named ? {
      ...init,
      ...named.seed
    } : init;
    $[7] = named;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const [state, dispatch] = useReducer(addFactiiiReducer, t4, t5);
  const requirements = collectRequirements(state, forcedRequirements);
  const {
    data: nameAvailable,
    isLoading: isCheckingName
  } = useQuery({
    queryKey: ["factiiiNameAvailability", state.name, state.spaceSlug, requirements],
    queryFn: async () => {
      const data = await unwrapFromResult(client.factiiis.checkNameAvailability.query({
        factiiiName: state.name,
        spaceSlug: state.spaceSlug || undefined,
        requirements
      }));
      return data.available;
    },
    enabled: state.name.length >= 3,
    retry: false
  });
  let t6;
  if ($[9] !== dispatch || $[10] !== setView) {
    t6 = picked => {
      setTemplate(picked);
      dispatch({
        type: "patch",
        patch: picked.seed
      });
      setView({
        name: "form"
      });
    };
    $[9] = dispatch;
    $[10] = setView;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const pickTemplate = t6;
  const submitHandler = async () => {
    setSubmitting(true);
    const [error, newFactiii] = await client.factiiis.add.mutate(toAddInput(state, requirements));
    setSubmitting(false);
    if (error || !newFactiii) {
      toast(error?.message || "Failed to create factiii", "", "error");
      return;
    }
    addFactiii(newFactiii);
    props.refetch();
    props.closeHandler(newFactiii);
  };
  const canCreate = state.name.trim().length >= 3 && Boolean(nameAvailable) && !submitting && !rootNeedsRule(state, requirements);
  let t7;
  if ($[12] !== view.key || $[13] !== view.name) {
    t7 = view.name === "section" ? findSection(view.key) : null;
    $[12] = view.key;
    $[13] = view.name;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const section = t7;
  let t8;
  if ($[15] !== preset || $[16] !== setView || $[17] !== view.from || $[18] !== view.name) {
    t8 = () => {
      if (view.name === "section") {
        setView(view.from === "form" ? {
          name: "form"
        } : {
          name: "options"
        });
      } else {
        if (view.name === "options") {
          setView({
            name: "form"
          });
        } else {
          if (view.name === "form" && !preset) {
            setView({
              name: "gallery"
            });
          }
        }
      }
    };
    $[15] = preset;
    $[16] = setView;
    $[17] = view.from;
    $[18] = view.name;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const back = t8;
  const showBack = view.name !== "gallery" && !(view.name === "form" && preset);
  const title = section?.title ?? (view.name === "options" ? "Options" : "Add Factiii");
  const subtitle = view.name === "gallery" ? "Pick what kind of factiii this is" : view.name === "form" ? props.parent ? `A sub factiii under ${props.parent.name}` : "Give it a name \u2014 that is all it needs" : view.name === "options" ? "Everything here is optional" : state.name || template.title;
  let t9;
  if ($[20] !== back || $[21] !== props.parent || $[22] !== showBack) {
    t9 = showBack ? <button type="button" onClick={back} aria-label="Back" className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-ds-border-standard bg-background-secondary text-primary transition-colors hover:border-ds-border-hover hover:bg-primary/5"><ChevronLeft size={15} /></button> : props.parent ? <Avatar factiiiAvatarKey={props.parent.avatarKey} factiiiId={props.parent.id} size={32} /> : <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-ds-border-standard bg-background-secondary text-primary"><Plus size={15} /></span>;
    $[20] = back;
    $[21] = props.parent;
    $[22] = showBack;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== title) {
    t10 = <h2 className={`truncate ${t.title}`}>{title}</h2>;
    $[24] = title;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== subtitle) {
    t11 = <p className={`truncate ${t.meta}`}>{subtitle}</p>;
    $[26] = subtitle;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <div className="min-w-0">{t10}{t11}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== t9) {
    t13 = <div className="flex min-w-0 items-center gap-2.5">{t9}{t12}</div>;
    $[31] = t12;
    $[32] = t9;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== props.closeHandler || $[35] !== t13) {
    t14 = <ModalHeader closeHandler={props.closeHandler}>{t13}</ModalHeader>;
    $[34] = props.closeHandler;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== pickTemplate || $[38] !== view.name) {
    t15 = view.name === "gallery" && <TemplateGallery onPick={pickTemplate} />;
    $[37] = pickTemplate;
    $[38] = view.name;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== props.parent || $[41] !== view.name) {
    t16 = view.name === "form" && props.parent && <div className="mb-3 flex items-center gap-2.5 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2"><CornerDownRight size={14} className="shrink-0 text-primary" /><Avatar factiiiAvatarKey={props.parent.avatarKey} factiiiId={props.parent.id} size={24} /><div className="min-w-0"><p className={`truncate ${t.label}`}>Under {props.parent.name}</p><p className={t.meta}>AI asks about this one only after “{props.parent.name}” fires on a file.</p></div></div>;
    $[40] = props.parent;
    $[41] = view.name;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== dispatch || $[44] !== forcedRequirements || $[45] !== isCheckingName || $[46] !== nameAvailable || $[47] !== preset || $[48] !== setView || $[49] !== state || $[50] !== template || $[51] !== view.name) {
    t17 = view.name === "form" && <FactiiiForm state={state} dispatch={dispatch} template={template} forcedRequirements={forcedRequirements} nameAvailable={nameAvailable} isCheckingName={isCheckingName} showTemplateBar={!preset} onChangeTemplate={() => setView({
      name: "gallery"
    })} onOpenOptions={() => setView({
      name: "options"
    })} onOpenSection={key => setView({
      name: "section",
      key,
      from: "form"
    })} />;
    $[43] = dispatch;
    $[44] = forcedRequirements;
    $[45] = isCheckingName;
    $[46] = nameAvailable;
    $[47] = preset;
    $[48] = setView;
    $[49] = state;
    $[50] = template;
    $[51] = view.name;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== dispatch || $[54] !== props.type || $[55] !== setView || $[56] !== state || $[57] !== view.name) {
    t18 = view.name === "options" && <OptionsView state={state} dispatch={dispatch} type={props.type} onOpen={key_0 => setView({
      name: "section",
      key: key_0,
      from: "options"
    })} />;
    $[53] = dispatch;
    $[54] = props.type;
    $[55] = setView;
    $[56] = state;
    $[57] = view.name;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== dispatch || $[60] !== section || $[61] !== state) {
    t19 = section && SECTION_BODIES[section.key]({
      state,
      dispatch
    });
    $[59] = dispatch;
    $[60] = section;
    $[61] = state;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== t15 || $[64] !== t16 || $[65] !== t17 || $[66] !== t18 || $[67] !== t19) {
    t20 = <ModalContent lockHeight="compact">{t15}{t16}{t17}{t18}{t19}</ModalContent>;
    $[63] = t15;
    $[64] = t16;
    $[65] = t17;
    $[66] = t18;
    $[67] = t19;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== props) {
    t21 = <Button text="Cancel" variant="SECONDARY" borderTransparent={false} onClick={() => props.closeHandler()} />;
    $[69] = props;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  let t22;
  if ($[71] !== back || $[72] !== canCreate || $[73] !== submitHandler || $[74] !== submitting || $[75] !== view.name) {
    t22 = view.name === "gallery" ? null : view.name === "form" ? <Button text="Create Factiii" disabled={!canCreate} loading={submitting} onClick={submitHandler} /> : <Button text="Done" onClick={back} />;
    $[71] = back;
    $[72] = canCreate;
    $[73] = submitHandler;
    $[74] = submitting;
    $[75] = view.name;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== t21 || $[78] !== t22) {
    t23 = <ModalFooter><div className="flex w-full items-center justify-between gap-3">{t21}{t22}</div></ModalFooter>;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  let t24;
  if ($[80] !== props.closeHandler || $[81] !== t14 || $[82] !== t20 || $[83] !== t23) {
    t24 = <Modal closeHandler={props.closeHandler}>{t14}{t20}{t23}</Modal>;
    $[80] = props.closeHandler;
    $[81] = t14;
    $[82] = t20;
    $[83] = t23;
    $[84] = t24;
  } else {
    t24 = $[84];
  }
  return t24;
};
export default AddFactiiiModal;